import { useBrand } from '@/config/brands';
import { TimelineEventCard } from './TimelineEventCard';
import type { LocalScenarioProcedure } from '@/lib/scenarioStorage';

interface TimelinePhaseColumnProps {
  phase: string;
  procedures: (LocalScenarioProcedure & { originalIndex: number })[];
  onRemoveProcedure: (index: number) => void;
  onEditProcedure: (index: number) => void;
  onDragStart: (e: React.DragEvent, index: number) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, targetPhase: string) => void;
  isDropTarget?: boolean;
}

export function TimelinePhaseColumn({
  phase,
  procedures,
  onRemoveProcedure,
  onEditProcedure,
  onDragStart,
  onDragOver,
  onDrop,
  isDropTarget,
}: TimelinePhaseColumnProps) {
  const brand = useBrand();
  const isAhp = brand.design === 'civic';

  const phaseTotal = procedures.reduce(
    (sum, p) => sum + p.estimatedCost * p.quantity,
    0
  );

  return (
    <div
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, phase)}
      className={`flex-1 min-w-[200px] rounded-xl border-2 transition-all ${
        isDropTarget
          ? isAhp
            ? 'border-civic-money bg-green-50/50'
            : 'border-matrix-green bg-matrix-green/5'
          : isAhp
            ? 'border-gray-200 bg-gray-50/50'
            : 'border-white/10 bg-black/20'
      }`}
    >
      {/* Phase header */}
      <div className={`px-3 py-2 border-b ${isAhp ? 'border-gray-200' : 'border-white/10'}`}>
        <div className="flex items-center justify-between">
          <h4 className={`text-xs font-bold uppercase tracking-wide ${
            isAhp ? 'text-civic-money' : 'text-matrix-green'
          }`}>
            {phase}
          </h4>
          <span className={`text-[10px] font-mono ${isAhp ? 'text-gray-500' : 'text-muted-foreground'}`}>
            {procedures.length} item{procedures.length !== 1 ? 's' : ''}
          </span>
        </div>
        {phaseTotal > 0 && (
          <p className={`text-xs font-mono mt-0.5 ${isAhp ? 'text-gray-700' : 'text-foreground/70'}`}>
            ${phaseTotal.toLocaleString()}
          </p>
        )}
      </div>

      {/* Procedure cards */}
      <div className="p-2 space-y-1.5 min-h-[60px]">
        {procedures.length === 0 ? (
          <div className="flex items-center justify-center h-[50px] text-[10px] text-muted-foreground/50 italic">
            Drop procedures here
          </div>
        ) : (
          procedures.map((proc) => (
            <TimelineEventCard
              key={`${proc.code}-${proc.originalIndex}`}
              procedure={proc}
              index={proc.originalIndex}
              onRemove={() => onRemoveProcedure(proc.originalIndex)}
              onEdit={() => onEditProcedure(proc.originalIndex)}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDrop={onDrop}
              compact
            />
          ))
        )}
      </div>
    </div>
  );
}
